import React, { useState, useEffect } from 'react';
import { X, DollarSign, PieChart, TrendingUp, Plus, AlertCircle, Sparkles, Download, Edit, ExternalLink, Building2, Minimize2, Maximize2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useEntity } from '@/hooks/useEntity';
import { budgetEntityConfig, spendingTrendEntityConfig, type Budget as BudgetType, type SpendingTrend as SpendingTrendType } from '@/entities/Budget';

type BudgetCategory = {
  id: string;
  name: string;
  budgeted: number;
  spent: number;
  type: 'fixed' | 'variable' | 'academic' | 'savings';
  icon: string;
  color: string;
};

type Expense = {
  id: string;
  categoryId: string;
  amount: number;
  description: string;
  date: string;
};

type Budget = {
  id: string;
  totalAmount: number;
  checkingBalance: number;
  savingsBalance: number;
  budgetFromAccount: 'checking' | 'savings' | 'both';
  startDate: string;
  endDate: string;
  categories: BudgetCategory[];
  expenses: Expense[];
  semesterName?: string;
};

type SpendingTrend = {
  semesterId: string;
  semesterName: string;
  totalBudget: number;
  totalSpent: number;
  startDate: string;
  endDate: string;
  topCategory: string;
  savedAmount: number;
};

type BudgetAssistantProps = {
  onClose: () => void;
  mode?: 'student' | 'personal'; // Add mode prop
};

const BANKS = [
  { name: 'Chase', url: 'https://www.chase.com/', logo: '🏦' },
  { name: 'Bank of America', url: 'https://www.bankofamerica.com/', logo: '🏦' },
  { name: 'Wells Fargo', url: 'https://www.wellsfargo.com/', logo: '🏦' },
  { name: 'Capital One', url: 'https://www.capitalone.com/', logo: '💳' },
  { name: 'Citibank', url: 'https://online.citi.com/', logo: '🏦' },
  { name: 'US Bank', url: 'https://www.usbank.com/', logo: '🏦' },
  { name: 'PNC Bank', url: 'https://www.pnc.com/', logo: '🏦' },
  { name: 'TD Bank', url: 'https://www.td.com/', logo: '🏦' },
  { name: 'Ally Bank', url: 'https://www.ally.com/', logo: '💰' },
  { name: 'Discover', url: 'https://www.discover.com/', logo: '💳' },
];

const BudgetAssistant: React.FC<BudgetAssistantProps> = ({ onClose, mode = 'student' }) => {
  const { user } = useAuth();
  const { items: budgets, create: createBudget, update: updateBudget, reload: reloadBudgets } = useEntity<BudgetType>(budgetEntityConfig);
  const { items: trends, create: createTrend, reload: reloadTrends } = useEntity<SpendingTrendType>(spendingTrendEntityConfig);
  
  const isPersonal = mode === 'personal';
  const [step, setStep] = useState<'welcome' | 'interview' | 'generation' | 'dashboard' | 'banks' | 'trends'>('welcome');
  const [budget, setBudget] = useState<Budget | null>(null);
  const [totalAmount, setTotalAmount] = useState('');
  const [checkingBalance, setCheckingBalance] = useState('');
  const [savingsBalance, setSavingsBalance] = useState('');
  const [budgetFromAccount, setBudgetFromAccount] = useState<'checking' | 'savings' | 'both'>('checking');
  const [semesterName, setSemesterName] = useState('');
  const [semesterStart, setSemesterStart] = useState('');
  const [semesterEnd, setSemesterEnd] = useState('');
  const [editingBudget, setEditingBudget] = useState(false);
  const [spendingTrends, setSpendingTrends] = useState<SpendingTrend[]>([]);
  const [interviewAnswers, setInterviewAnswers] = useState({
    living: '',
    mealPlan: '',
    transportation: '',
    workPartTime: '',
    international: '',
  });
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showBankDisclaimer, setShowBankDisclaimer] = useState(false);
  const [selectedBank, setSelectedBank] = useState<typeof BANKS[0] | null>(null);
  const [newExpense, setNewExpense] = useState({
    categoryId: '',
    amount: '',
    description: '',
  });

  // Load existing budget from database
  useEffect(() => {
    reloadBudgets();
    reloadTrends();
  }, [reloadBudgets, reloadTrends]);

  useEffect(() => {
    if (!user) return;
    
    // Filter budgets by userId
    const userBudgets = budgets.filter(b => b.userId === user.id);
    if (userBudgets.length > 0) {
      // Get most recent budget
      const latest = userBudgets.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0];
      setBudget(latest as any);
      setStep('dashboard');
    }
    
    // Filter trends by userId  
    const userTrends = trends.filter(t => t.userId === user.id);
    setSpendingTrends(userTrends as any[]);
  }, [budgets, trends, user]);

  const generateBudget = () => {
    const amount = parseFloat(totalAmount);
    const checking = parseFloat(checkingBalance) || 0;
    const savings = parseFloat(savingsBalance) || 0;
    const weeks = calculateWeeks(semesterStart, semesterEnd);
    
    // AI-generated budget based on answers
    const categories: BudgetCategory[] = [
      // Fixed expenses
      {
        id: 'rent',
        name: interviewAnswers.living === 'on-campus' ? 'Dorm Fees' : interviewAnswers.living === 'online' ? 'Utilities' : 'Rent',
        budgeted: interviewAnswers.living === 'family' ? 0 : interviewAnswers.living === 'online' ? amount * 0.10 : amount * 0.35,
        spent: 0,
        type: 'fixed',
        icon: '🏠',
        color: 'bg-blue-500',
      },
      {
        id: 'food',
        name: 'Food & Groceries',
        budgeted: interviewAnswers.mealPlan === 'yes' ? amount * 0.10 : amount * 0.20,
        spent: 0,
        type: 'variable',
        icon: '🍔',
        color: 'bg-green-500',
      },
      {
        id: 'transportation',
        name: 'Transportation',
        budgeted: interviewAnswers.transportation === 'drive' ? amount * 0.15 : interviewAnswers.living === 'online' ? amount * 0.02 : amount * 0.05,
        spent: 0,
        type: 'variable',
        icon: '🚗',
        color: 'bg-amber-500',
      },
      {
        id: 'textbooks',
        name: 'Textbooks & Supplies',
        budgeted: interviewAnswers.living === 'online' ? amount * 0.05 : amount * 0.08,
        spent: 0,
        type: 'academic',
        icon: '📚',
        color: 'bg-purple-500',
      },
      {
        id: 'technology',
        name: interviewAnswers.living === 'online' ? 'Internet & Tech' : 'Personal Care',
        budgeted: interviewAnswers.living === 'online' ? amount * 0.08 : amount * 0.05,
        spent: 0,
        type: interviewAnswers.living === 'online' ? 'fixed' : 'variable',
        icon: interviewAnswers.living === 'online' ? '💻' : '✨',
        color: 'bg-pink-500',
      },
      {
        id: 'entertainment',
        name: 'Entertainment',
        budgeted: amount * 0.07,
        spent: 0,
        type: 'variable',
        icon: '🎮',
        color: 'bg-cyan-500',
      },
      {
        id: 'emergency',
        name: 'Emergency Fund',
        budgeted: amount * 0.10,
        spent: 0,
        type: 'savings',
        icon: '🚨',
        color: 'bg-red-500',
      },
    ];

    const newBudget: Budget = {
      id: editingBudget && budget ? budget.id : Date.now().toString(),
      totalAmount: amount,
      checkingBalance: checking,
      savingsBalance: savings,
      budgetFromAccount,
      startDate: semesterStart,
      endDate: semesterEnd,
      categories: editingBudget && budget ? budget.categories.map(cat => ({
        ...cat,
        budgeted: categories.find(c => c.id === cat.id)?.budgeted || cat.budgeted
      })) : categories,
      expenses: editingBudget && budget ? budget.expenses : [],
      semesterName: semesterName || `Semester ${new Date(semesterStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`,
    };

    // Save old budget to trends before replacing (only for new budgets)
    if (budget && !editingBudget) {
      saveBudgetToTrends(budget);
    }

    setBudget(newBudget);
    
    // Save to database
    if (user) {
      const dbBudget: Partial<BudgetType> = {
        userId: user.id,
        totalAmount: newBudget.totalAmount,
        startDate: newBudget.startDate,
        endDate: newBudget.endDate,
        categories: newBudget.categories,
      };
      
      if (editingBudget && budget?.id) {
        updateBudget(budget.id as any, dbBudget as any);
      } else {
        createBudget(dbBudget as any);
      }
    }
    
    setEditingBudget(false);
    setStep('dashboard');
  };

  const saveBudgetToTrends = (completedBudget: Budget) => {
    const totalSpent = completedBudget.categories.reduce((sum, cat) => sum + cat.spent, 0);
    const topCategory = completedBudget.categories.reduce((max, cat) => 
      cat.spent > max.spent ? cat : max
    , completedBudget.categories[0]);

    const trend: SpendingTrend = {
      semesterId: completedBudget.id,
      semesterName: completedBudget.semesterName || 'Past Semester',
      totalBudget: completedBudget.totalAmount,
      totalSpent,
      startDate: completedBudget.startDate,
      endDate: completedBudget.endDate,
      topCategory: topCategory.name,
      savedAmount: completedBudget.totalAmount - totalSpent,
    };

    const updatedTrends = [...spendingTrends, trend];
    setSpendingTrends(updatedTrends);
    
    // Save trend to database
    if (user) {
      const dbTrend: Partial<SpendingTrendType> = {
        userId: user.id,
        month: trend.semesterId,
        totalBudget: trend.totalBudget,
        totalSpent: trend.totalSpent,
        savedAmount: trend.savedAmount,
      };
      createTrend(dbTrend as any);
    }
  };

  const calculateWeeks = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.ceil((endDate.getTime() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
  };

  const addExpense = () => {
    if (!budget || !newExpense.categoryId || !newExpense.amount) return;

    const expense: Expense = {
      id: Date.now().toString(),
      categoryId: newExpense.categoryId,
      amount: parseFloat(newExpense.amount),
      description: newExpense.description,
      date: new Date().toISOString(),
    };

    const updatedBudget = {
      ...budget,
      expenses: [...budget.expenses, expense],
      categories: budget.categories.map(cat =>
        cat.id === expense.categoryId
          ? { ...cat, spent: cat.spent + expense.amount }
          : cat
      ),
    };

    setBudget(updatedBudget);
    
    // Update in database
    if (user && budget?.id) {
      const dbBudget: Partial<BudgetType> = {
        userId: user.id,
        totalAmount: updatedBudget.totalAmount,
        startDate: updatedBudget.startDate,
        endDate: updatedBudget.endDate,
        categories: updatedBudget.categories,
      };
      updateBudget(budget.id as any, dbBudget as any);
    }
    
    setNewExpense({ categoryId: '', amount: '', description: '' });
    setShowAddExpense(false);
  };

  const totalSpent = budget?.categories.reduce((sum, cat) => sum + cat.spent, 0) || 0;
  const totalBudgeted = budget?.totalAmount || 0;
  const remaining = totalBudgeted - totalSpent;
  const percentSpent = totalBudgeted > 0 ? (totalSpent / totalBudgeted) * 100 : 0;

  const handleBankClick = (bank: typeof BANKS[0]) => {
    setSelectedBank(bank);
    setShowBankDisclaimer(true);
  };

  const confirmBankVisit = () => {
    if (selectedBank) {
      window.open(selectedBank.url, '_blank', 'noopener,noreferrer');
      setShowBankDisclaimer(false);
      setSelectedBank(null);
    }
  };

  const startEditingBudget = () => {
    if (budget) {
      setTotalAmount(budget.totalAmount.toString());
      setCheckingBalance((budget.checkingBalance || 0).toString());
      setSavingsBalance((budget.savingsBalance || 0).toString());
      setBudgetFromAccount(budget.budgetFromAccount || 'checking');
      setSemesterName(budget.semesterName || '');
      setSemesterStart(budget.startDate);
      setSemesterEnd(budget.endDate);
      setEditingBudget(true);
      setStep('interview');
    }
  };

  // Calculate which balance to use
  const getAvailableBalance = () => {
    if (!budget) return 0;
    const checking = budget.checkingBalance || 0;
    const savings = budget.savingsBalance || 0;
    
    if (budget.budgetFromAccount === 'checking') return checking;
    if (budget.budgetFromAccount === 'savings') return savings;
    return checking + savings; // both
  };

  const totalAvailable = (budget?.totalAmount || 0) + getAvailableBalance();

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      {/* Fun Animations */}
      <style>{`
        @keyframes twirl {
          0%, 100% { transform: rotate(0deg) scale(1); }
          25% { transform: rotate(10deg) scale(1.1); }
          50% { transform: rotate(-10deg) scale(1.15); }
          75% { transform: rotate(5deg) scale(1.1); }
        }
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(16,185,129,0.3); }
          50% { box-shadow: 0 0 40px rgba(16,185,129,0.6), 0 0 60px rgba(16,185,129,0.4); }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        .icon-twirl:hover {
          animation: twirl 0.6s ease-in-out;
        }
        .category-card:hover {
          animation: glow-pulse 1.5s ease-in-out infinite;
        }
        .shimmer-bg {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
          background-size: 1000px 100%;
          animation: shimmer 2s infinite;
        }
      `}</style>
      <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-emerald-400/30 rounded-3xl w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-emerald-400/30">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{isPersonal ? 'Personal Budget Assistant' : 'Student Budget Assistant'}</h2>
              <p className="text-sm text-emerald-200">AI-powered financial planning</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'welcome' && (
            <div className="max-w-2xl mx-auto text-center space-y-6">
              <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-5xl icon-twirl cursor-pointer">
                💰
              </div>
              <h3 className="text-3xl font-bold text-white">Welcome to Budget Assistant!</h3>
              <p className="text-lg text-slate-300">
                {isPersonal 
                  ? "Let's create a personalized budget for your finances. I'll ask you a few questions and help you manage your money wisely."
                  : "Let's create a personalized budget for your semester. I'll ask you a few questions and help you manage your financial aid wisely."
                }
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <div className="rounded-xl bg-emerald-900/20 border border-emerald-400/30 p-4 category-card transition-all hover:scale-105 cursor-pointer">
                  <div className="text-3xl mb-2 icon-twirl inline-block">📊</div>
                  <h4 className="font-bold text-white mb-1">Smart Categories</h4>
                  <p className="text-sm text-slate-300">AI suggests budget categories based on your lifestyle</p>
                </div>
                <div className="rounded-xl bg-blue-900/20 border border-blue-400/30 p-4 category-card transition-all hover:scale-105 cursor-pointer">
                  <div className="text-3xl mb-2 icon-twirl inline-block">📈</div>
                  <h4 className="font-bold text-white mb-1">Track Spending</h4>
                  <p className="text-sm text-slate-300">Log expenses and see real-time progress</p>
                </div>
                <div className="rounded-xl bg-purple-900/20 border border-purple-400/30 p-4 category-card transition-all hover:scale-105 cursor-pointer">
                  <div className="text-3xl mb-2 icon-twirl inline-block">💡</div>
                  <h4 className="font-bold text-white mb-1">Smart Tips</h4>
                  <p className="text-sm text-slate-300">Get personalized money-saving advice</p>
                </div>
              </div>

              <button
                onClick={() => setStep('interview')}
                className="mt-8 px-8 py-4 rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-bold text-lg transition-all hover:scale-105 shimmer-bg"
              >
                Let's Get Started! 🚀
              </button>
            </div>
          )}

          {step === 'interview' && (
            <div className="max-w-2xl mx-auto space-y-6">
              <h3 className="text-2xl font-bold text-white mb-6">{isPersonal ? 'Tell me about your budget period' : 'Tell me about your semester'}</h3>
              
              {/* Semester Name */}
              <div className="rounded-xl bg-purple-900/20 border border-purple-400/30 p-6">
                <label className="block text-purple-200 font-semibold mb-2">
                  <span className="icon-twirl inline-block">📚</span> {isPersonal ? 'Budget Period Name (Optional)' : 'Semester Name (Optional)'}
                </label>
                <input
                  type="text"
                  value={semesterName}
                  onChange={(e) => setSemesterName(e.target.value)}
                  placeholder="Fall 2026"
                  className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-400 focus:border-purple-400 focus:outline-none"
                />
              </div>

              {/* Current Bank Balances */}
              <div className="rounded-xl bg-blue-900/20 border border-blue-400/30 p-6">
                <label className="block text-blue-200 font-semibold mb-3">
                  <span className="icon-twirl inline-block">🏦</span> Bank Account Balances
                </label>
                <p className="text-xs text-blue-300 mb-4">Enter your current balance(s)</p>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-blue-200 mb-2">Checking Account</label>
                    <input
                      type="number"
                      value={checkingBalance}
                      onChange={(e) => setCheckingBalance(e.target.value)}
                      placeholder="500"
                      className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-400 focus:border-blue-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-blue-200 mb-2">Savings Account</label>
                    <input
                      type="number"
                      value={savingsBalance}
                      onChange={(e) => setSavingsBalance(e.target.value)}
                      placeholder="1000"
                      className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-400 focus:border-blue-400 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-blue-200 mb-2">Which account(s) will you use for this semester?</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 'checking', label: 'Checking Only', icon: '💳' },
                      { value: 'savings', label: 'Savings Only', icon: '🏦' },
                      { value: 'both', label: 'Both Accounts', icon: '💰' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => setBudgetFromAccount(option.value as any)}
                        className={`px-3 py-2 rounded-lg font-semibold transition-all hover:scale-105 text-sm ${
                          budgetFromAccount === option.value
                            ? 'bg-blue-500 text-white shadow-lg'
                            : 'bg-white/5 text-slate-300 hover:bg-white/10'
                        }`}
                      >
                        <div className="icon-twirl inline-block text-lg">{option.icon}</div>
                        <div className="text-xs mt-1">{option.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Total Amount */}
              <div className="rounded-xl bg-emerald-900/20 border border-emerald-400/30 p-6">
                <label className="block text-emerald-200 font-semibold mb-2">
                  <span className="icon-twirl inline-block">💰</span> {isPersonal ? 'Total Budget Amount' : 'Financial Aid Amount (This Semester)'}
                </label>
                <p className="text-xs text-emerald-300 mb-2">{isPersonal ? 'How much money do you have available for this budget period?' : 'How much financial aid are you receiving this semester?'}</p>
                <input
                  type="number"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  placeholder="5000"
                  className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-400 focus:border-emerald-400 focus:outline-none"
                />
                {totalAmount && (checkingBalance || savingsBalance) && (
                  <p className="text-xs text-emerald-300 mt-2">
                    💡 <strong>Total Available:</strong> ${(() => {
                      const aid = parseFloat(totalAmount) || 0;
                      const checking = parseFloat(checkingBalance) || 0;
                      const savings = parseFloat(savingsBalance) || 0;
                      const bankTotal = budgetFromAccount === 'checking' ? checking :
                                       budgetFromAccount === 'savings' ? savings :
                                       checking + savings;
                      return (aid + bankTotal).toLocaleString();
                    })()}
                    {budgetFromAccount === 'checking' && ' (using Checking)'}
                    {budgetFromAccount === 'savings' && ' (using Savings)'}
                    {budgetFromAccount === 'both' && ' (using Both)'}
                  </p>
                )}
              </div>

              {/* Semester Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl bg-blue-900/20 border border-blue-400/30 p-6">
                  <label className="block text-blue-200 font-semibold mb-2">📅 {isPersonal ? 'Budget Start Date' : 'Semester Start'}</label>
                  <input
                    type="date"
                    value={semesterStart}
                    onChange={(e) => setSemesterStart(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white focus:border-blue-400 focus:outline-none"
                  />
                </div>
                <div className="rounded-xl bg-blue-900/20 border border-blue-400/30 p-6">
                  <label className="block text-blue-200 font-semibold mb-2">📅 {isPersonal ? 'Budget End Date' : 'Semester End'}</label>
                  <input
                    type="date"
                    value={semesterEnd}
                    onChange={(e) => setSemesterEnd(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white focus:border-blue-400 focus:outline-none"
                  />
                </div>
              </div>

              {/* Interview Questions */}
              <div className="space-y-4">
                <div className="rounded-xl bg-purple-900/20 border border-purple-400/30 p-6 category-card">
                  <label className="block text-purple-200 font-semibold mb-3">
                    <span className="icon-twirl inline-block">🏠</span> {isPersonal ? 'What is your living situation?' : 'Where do you live / study?'}
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(isPersonal ? ['own', 'rent', 'family', 'other'] : ['on-campus', 'off-campus', 'online', 'family']).map(option => (
                      <button
                        key={option}
                        onClick={() => setInterviewAnswers({...interviewAnswers, living: option})}
                        className={`px-4 py-3 rounded-lg font-semibold transition-all hover:scale-105 ${
                          interviewAnswers.living === option
                            ? 'bg-purple-500 text-white shadow-lg'
                            : 'bg-white/5 text-slate-300 hover:bg-white/10'
                        }`}
                      >
                        <div className="icon-twirl inline-block">
                          {isPersonal 
                            ? (option === 'own' ? '🏡' : option === 'rent' ? '🏘️' : option === 'family' ? '👨‍👩‍👧‍👦' : '🏠')
                            : (option === 'on-campus' ? '🏫' : option === 'off-campus' ? '🏘️' : option === 'online' ? '💻' : '🏡')
                          }
                        </div>
                        <div className="text-xs mt-1">
                          {isPersonal
                            ? (option === 'own' ? 'Own Home' : option === 'rent' ? 'Renting' : option === 'family' ? 'With Family' : 'Other')
                            : (option === 'on-campus' ? 'On Campus' : option === 'off-campus' ? 'Off Campus' : option === 'online' ? 'Online School' : 'With Family')
                          }
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl bg-amber-900/20 border border-amber-400/30 p-6 category-card">
                  <label className="block text-amber-200 font-semibold mb-3">
                    <span className="icon-twirl inline-block">🍽️</span> {isPersonal ? 'How often do you buy groceries?' : 'Do you have a meal plan?'}
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {(isPersonal ? ['weekly', 'biweekly', 'monthly'] : ['yes', 'no', 'partial']).map(option => (
                      <button
                        key={option}
                        onClick={() => setInterviewAnswers({...interviewAnswers, mealPlan: option})}
                        className={`px-4 py-3 rounded-lg font-semibold transition-all capitalize hover:scale-105 ${
                          interviewAnswers.mealPlan === option
                            ? 'bg-amber-500 text-white shadow-lg'
                            : 'bg-white/5 text-slate-300 hover:bg-white/10'
                        }`}
                      >
                        <div className="icon-twirl inline-block">
                          {isPersonal
                            ? (option === 'weekly' ? '📅' : option === 'biweekly' ? '📆' : '📆')
                            : (option === 'yes' ? '✅' : option === 'no' ? '❌' : '➗')
                          }
                        </div>
                        <div className="text-xs mt-1">{isPersonal ? (option === 'biweekly' ? 'Every 2 Weeks' : option) : option}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl bg-cyan-900/20 border border-cyan-400/30 p-6 category-card">
                  <label className="block text-cyan-200 font-semibold mb-3">
                    <span className="icon-twirl inline-block">🚗</span> {isPersonal ? 'How do you usually get around?' : 'How do you get to school?'}
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {['walk', 'drive', 'transit'].map(option => (
                      <button
                        key={option}
                        onClick={() => setInterviewAnswers({...interviewAnswers, transportation: option})}
                        className={`px-4 py-3 rounded-lg font-semibold transition-all capitalize hover:scale-105 ${
                          interviewAnswers.transportation === option
                            ? 'bg-cyan-500 text-white shadow-lg'
                            : 'bg-white/5 text-slate-300 hover:bg-white/10'
                        }`}
                      >
                        <div className="icon-twirl inline-block">
                          {option === 'walk' ? '🚶' : option === 'drive' ? '🚗' : '🚌'}
                        </div>
                        <div className="text-xs mt-1">{option === 'transit' ? 'Public Transit' : option}</div>
                      </button>
                    ))}
                  </div>
                  {!isPersonal && interviewAnswers.living === 'online' && (
                    <p className="text-xs text-cyan-300 mt-2 italic">
                      💡 Tip: As an online student, you'll save on transportation costs!
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={generateBudget}
                disabled={!totalAmount || !semesterStart || !semesterEnd}
                className="w-full px-6 py-4 rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-lg transition-all hover:scale-105 flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5 icon-twirl" />
                {editingBudget ? 'Update My Budget' : 'Generate My Budget'}
              </button>
            </div>
          )}

          {step === 'dashboard' && budget && (
            <div className="space-y-6">
              {/* Semester Name Badge */}
              {budget.semesterName && (
                <div className="text-center">
                  <span className="px-4 py-2 rounded-full bg-purple-500/20 border border-purple-400/30 text-purple-200 font-semibold text-sm">
                    📚 {budget.semesterName}
                  </span>
                </div>
              )}

              {/* Summary Card */}
              <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 p-6 text-white">
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div>
                    <p className="text-xs text-emerald-100 mb-1">💳 Checking</p>
                    <p className="text-lg font-bold">${(budget.checkingBalance || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-emerald-100 mb-1">🏦 Savings</p>
                    <p className="text-lg font-bold">${(budget.savingsBalance || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-emerald-100 mb-1">💰 Aid</p>
                    <p className="text-lg font-bold">${totalBudgeted.toFixed(2)}</p>
                  </div>
                </div>

                <div className="rounded-lg bg-white/10 p-3 mb-4">
                  <p className="text-xs text-emerald-100 mb-1">
                    Using: {budget.budgetFromAccount === 'checking' ? '💳 Checking Only' :
                            budget.budgetFromAccount === 'savings' ? '🏦 Savings Only' :
                            '💰 Both Accounts'}
                  </p>
                  <p className="text-sm font-semibold">
                    Available Balance: ${getAvailableBalance().toFixed(2)}
                  </p>
                </div>
                
                <div className="border-t border-white/20 pt-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-emerald-100">Total Available</p>
                      <h3 className="text-3xl font-bold">${totalAvailable.toFixed(2)}</h3>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-emerald-100">Remaining</p>
                      <p className="text-2xl font-bold">${(totalAvailable - totalSpent).toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden">
                  <div
                    className="bg-white h-full rounded-full transition-all"
                    style={{ width: `${Math.min((totalSpent / totalAvailable) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-sm text-emerald-100 mt-2">
                  {((totalSpent / totalAvailable) * 100).toFixed(0)}% spent • {calculateWeeks(budget.startDate, budget.endDate)} weeks remaining
                </p>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <button
                  onClick={() => setShowAddExpense(true)}
                  className="px-4 py-3 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 text-emerald-200 font-semibold transition-all hover:scale-105 flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5 icon-twirl" />
                  Add Expense
                </button>
                <button
                  onClick={() => setStep('trends')}
                  className="px-4 py-3 rounded-xl bg-pink-500/20 hover:bg-pink-500/30 border border-pink-400/30 text-pink-200 font-semibold transition-all hover:scale-105 flex items-center gap-2"
                >
                  <TrendingUp className="w-4 h-4 icon-twirl" />
                  Trends
                </button>
                <button
                  onClick={startEditingBudget}
                  className="px-4 py-3 rounded-xl bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 text-purple-200 font-semibold transition-all hover:scale-105 flex items-center gap-2"
                >
                  <Edit className="w-4 h-4 icon-twirl" />
                  Edit Budget
                </button>
                <button
                  onClick={() => setStep('banks')}
                  className="px-4 py-3 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 text-blue-200 font-semibold transition-all hover:scale-105 flex items-center gap-2"
                >
                  <Building2 className="w-4 h-4 icon-twirl" />
                  Bank Access
                </button>
                <button
                  onClick={() => {
                    // Export functionality
                    const csv = "Category,Budgeted,Spent,Remaining\n" +
                      budget.categories.map(cat =>
                        `${cat.name},${cat.budgeted},${cat.spent},${cat.budgeted - cat.spent}`
                      ).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'budget.csv';
                    a.click();
                  }}
                  className="px-4 py-3 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/30 text-amber-200 font-semibold transition-all hover:scale-105 flex items-center gap-2"
                >
                  <Download className="w-4 h-4 icon-twirl" />
                  Export
                </button>
              </div>

              {/* Categories */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {budget.categories.map(category => {
                  const percentage = category.budgeted > 0 ? (category.spent / category.budgeted) * 100 : 0;
                  const remaining = category.budgeted - category.spent;
                  const status = percentage > 90 ? 'danger' : percentage > 75 ? 'warning' : 'good';

                  return (
                    <div
                      key={category.id}
                      className="rounded-xl bg-white/5 border border-white/10 hover:border-white/20 p-5 category-card transition-all hover:scale-105 cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl icon-twirl inline-block">{category.icon}</span>
                          <div>
                            <h4 className="font-bold text-white">{category.name}</h4>
                            <p className="text-xs text-slate-400 capitalize">{category.type}</p>
                          </div>
                        </div>
                        {status === 'danger' && <AlertCircle className="w-5 h-5 text-red-400" />}
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-300">${category.spent.toFixed(2)} spent</span>
                          <span className="text-white font-semibold">${category.budgeted.toFixed(2)} budgeted</span>
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              status === 'danger' ? 'bg-red-500' :
                              status === 'warning' ? 'bg-amber-500' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                        <p className={`text-sm font-semibold ${
                          remaining < 0 ? 'text-red-300' : 'text-emerald-300'
                        }`}>
                          ${Math.abs(remaining).toFixed(2)} {remaining < 0 ? 'over budget' : 'remaining'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 'trends' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-3xl icon-twirl mb-4">
                  📊
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Spending Trends</h3>
                <p className="text-slate-300">See how your spending evolves semester by semester</p>
              </div>

              {spendingTrends.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-slate-400 mb-4">No spending history yet</p>
                  <p className="text-sm text-slate-500">Complete your first semester to see trends!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Trends Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="rounded-xl bg-emerald-900/20 border border-emerald-400/30 p-4 text-center">
                      <p className="text-sm text-emerald-200 mb-1">Total Semesters</p>
                      <p className="text-3xl font-bold text-white">{spendingTrends.length}</p>
                    </div>
                    <div className="rounded-xl bg-blue-900/20 border border-blue-400/30 p-4 text-center">
                      <p className="text-sm text-blue-200 mb-1">Average Spent</p>
                      <p className="text-3xl font-bold text-white">
                        ${(spendingTrends.reduce((sum, t) => sum + t.totalSpent, 0) / spendingTrends.length).toFixed(0)}
                      </p>
                    </div>
                    <div className="rounded-xl bg-purple-900/20 border border-purple-400/30 p-4 text-center">
                      <p className="text-sm text-purple-200 mb-1">Total Saved</p>
                      <p className="text-3xl font-bold text-white">
                        ${spendingTrends.reduce((sum, t) => sum + t.savedAmount, 0).toFixed(0)}
                      </p>
                    </div>
                  </div>

                  {/* Individual Semester Cards */}
                  {spendingTrends.map((trend, index) => {
                    const percentSpent = (trend.totalSpent / trend.totalBudget) * 100;
                    const saved = trend.savedAmount >= 0;

                    return (
                      <div
                        key={trend.semesterId}
                        className="rounded-xl bg-white/5 border border-white/10 hover:border-white/20 p-5 category-card transition-all hover:scale-105"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-bold text-white text-lg">{trend.semesterName}</h4>
                            <p className="text-xs text-slate-400">
                              {new Date(trend.startDate).toLocaleDateString()} - {new Date(trend.endDate).toLocaleDateString()}
                            </p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            saved ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'
                          }`}>
                            {saved ? '✅ Saved' : '⚠️ Overspent'}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-3">
                          <div>
                            <p className="text-xs text-slate-400">Budget</p>
                            <p className="text-lg font-semibold text-white">${trend.totalBudget.toFixed(0)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400">Spent</p>
                            <p className="text-lg font-semibold text-white">${trend.totalSpent.toFixed(0)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400">{saved ? 'Saved' : 'Over'}</p>
                            <p className={`text-lg font-semibold ${saved ? 'text-emerald-300' : 'text-red-300'}`}>
                              ${Math.abs(trend.savedAmount).toFixed(0)}
                            </p>
                          </div>
                        </div>

                        <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden mb-2">
                          <div
                            className={`h-full rounded-full transition-all ${
                              percentSpent > 100 ? 'bg-red-500' : 
                              percentSpent > 90 ? 'bg-amber-500' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${Math.min(percentSpent, 100)}%` }}
                          />
                        </div>

                        <div className="flex items-center justify-between text-xs text-slate-400">
                          <span>{percentSpent.toFixed(0)}% of budget</span>
                          <span>Top: {trend.topCategory}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                onClick={() => setStep('dashboard')}
                className="w-full px-6 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-semibold transition-all"
              >
                ← Back to Dashboard
              </button>
            </div>
          )}

          {step === 'banks' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-3xl icon-twirl mb-4">
                  🏦
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Quick Bank Access</h3>
                <p className="text-slate-300">Click any bank to access your online banking</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {BANKS.map((bank) => (
                  <button
                    key={bank.name}
                    onClick={() => handleBankClick(bank)}
                    className="rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-blue-400/50 p-4 transition-all hover:scale-105 category-card"
                  >
                    <div className="text-4xl mb-2 icon-twirl">{bank.logo}</div>
                    <p className="text-sm font-semibold text-white">{bank.name}</p>
                  </button>
                ))}
              </div>

              <div className="rounded-xl bg-blue-900/20 border border-blue-400/30 p-4 text-center">
                <p className="text-sm text-blue-200">
                  💡 <strong>Tip:</strong> Keep track of your spending by checking your bank balance regularly!
                </p>
              </div>

              <button
                onClick={() => setStep('dashboard')}
                className="w-full px-6 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-semibold transition-all"
              >
                ← Back to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bank Disclaimer Modal */}
      {showBankDisclaimer && selectedBank && (
        <div className="fixed inset-0 z-[10002] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-blue-400/30 rounded-2xl w-full max-w-md p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/20 flex items-center justify-center text-3xl mb-4">
                ⚠️
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Leaving WHIPT</h3>
              <p className="text-sm text-slate-300">
                You're about to visit <strong className="text-blue-300">{selectedBank.name}</strong> in a new window.
              </p>
            </div>

            <div className="rounded-xl bg-amber-900/20 border border-amber-400/30 p-4 mb-6">
              <p className="text-xs text-amber-200 leading-relaxed">
                <strong>Important Disclaimer:</strong> WHIPT is not affiliated with {selectedBank.name}. 
                We do not have access to your banking information. This is simply a convenient link to help you access your bank's website.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowBankDisclaimer(false);
                  setSelectedBank(null);
                }}
                className="flex-1 px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmBankVisit}
                className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold transition-all flex items-center justify-center gap-2"
              >
                Continue <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showAddExpense && budget && (
        <div className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-emerald-400/30 rounded-2xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-white mb-4">Add Expense</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-emerald-200 font-semibold mb-2">Amount</label>
                <input
                  type="number"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                  placeholder="25.00"
                  className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-400 focus:border-emerald-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-emerald-200 font-semibold mb-2">Category</label>
                <select
                  value={newExpense.categoryId}
                  onChange={(e) => setNewExpense({...newExpense, categoryId: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white focus:border-emerald-400 focus:outline-none"
                >
                  <option value="">Select category...</option>
                  {budget.categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-emerald-200 font-semibold mb-2">Description (Optional)</label>
                <input
                  type="text"
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                  placeholder="Coffee with friends"
                  className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-400 focus:border-emerald-400 focus:outline-none"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAddExpense(false)}
                  className="flex-1 px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={addExpense}
                  disabled={!newExpense.categoryId || !newExpense.amount}
                  className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-all"
                >
                  Add Expense
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetAssistant;
